import React from 'react';
import styled from 'styled-components';
import { ModalOverlay } from 'components/common/modal-overlay';
import { ModalHeader } from 'components/common/modal-overlay/ModalHeader';
import Loading from 'components/common/Loading';
import { FlashMessageContext } from 'components/app-context/FlashMessageContextProvider';
import { MatterFilters } from './MatterFilters';
import { FilteredMatters } from './FilteredMatters';
import { useAddMatterItems } from './useAddMatterItems';
import FormAddedFlashMessage from '../true-form/FormAddedFlashMessage';

interface AddToMatterModalProps {
  onSave?: (selected: any) => void;
  visible?: boolean;
  closeModal: (e: React.MouseEvent) => void;
  type?: string;
}
function AddToMatterModal({
  onSave,
  visible,
  closeModal,
  type = 'Form',
}: AddToMatterModalProps) {
  const [filter, setFilter] = React.useState(undefined);
  const [byRecent, setByRecent] = React.useState(true);
  const [processing, setProcessing] = React.useState(false);
  const [selected, setSelected] = React.useState(undefined);

  const toggleRecent = React.useCallback(() => {
    setByRecent(v => !v);
  }, [setByRecent]);

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      setProcessing(true);
      setSelected(undefined);
      onSave && onSave(selected);
      closeModal(e);
      setProcessing(false);
    },
    [setProcessing, setSelected, onSave, selected, closeModal],
  );

  return (
    <ModalOverlay
      visible={visible}
      closeModal={
        closeModal as unknown as (
          event?: React.SyntheticEvent<HTMLElement, Event> | undefined,
        ) => void
      }
      maxWidth="660px"
      maxHeight="680px"
    >
      <StyledMatterModal>
        <StyledModalHeader close={closeModal}>
          <h2>Add to Client Matter</h2>
        </StyledModalHeader>
        <section>
          <div>
            <div>
              <p>Choose a Matter to add this {type} to:</p>
            </div>
            <MatterFilters
              filter={filter}
              setFilter={setFilter}
              byRecent={byRecent}
              toggleRecent={toggleRecent}
            />
          </div>
          <div className="filtered-results">
            <Loading>
              <FilteredMatters
                selected={selected}
                setSelected={setSelected}
                byRecent={byRecent}
                filter={filter}
              />
            </Loading>
          </div>
          <div className="button-row">
            <StyledPrimaryModalButton
              disabled={!selected || processing}
              onClick={handleClick}
            >
              {processing && <i className="fa fa-spinner fa-spin" />} Add to
              Selected Matter
            </StyledPrimaryModalButton>
            <StyledModalButton onClick={closeModal}>Cancel</StyledModalButton>
          </div>
        </section>
      </StyledMatterModal>
    </ModalOverlay>
  );
}

interface AddFormToMatterModal {
  form: any;
  visible?: boolean;
  closeModal: (e: React.MouseEvent) => void;
}

export function AddFormToMatterModal({
  form,
  visible,
  closeModal,
}: AddFormToMatterModal) {
  const { addMessage } = React.useContext(FlashMessageContext);

  const afterSave = React.useCallback(
    (selected: any, items: any[]) => {
      addMessage({
        icon: 'fas fa-check-circle',
        type: 'success',
        dismissIcon: 'fa fa-times',
        autoDismiss: true,
        component: 'FormAddedFlashMessage',
        props: {
          matterName: selected.name,
          matterUrl: `/matters/matter/${selected.id}`,
          matterFormUrl: `/matters/matter/${selected.id}/documents_and_forms/${form.slug}/${items[0].id}`,
        },
      });
    },
    [form, addMessage],
  );

  const addMatterItems = useAddMatterItems(afterSave, [
    {
      contentfulId: form.sys.id,
      document: {
        docspringTemplateId: form.docspringTemplateId,
      },
    },
  ]);

  return (
    <AddToMatterModal
      onSave={addMatterItems}
      visible={visible}
      closeModal={closeModal}
      type="Form"
    />
  );
}

interface AddGroupToMatterModalProps {
  group: any;
  visible?: boolean;
  closeModal: (e: React.MouseEvent) => void;
  afterSave: (selected: any) => void;
}

export function AddGroupToMatterModal({
  group,
  visible,
  closeModal,
  afterSave,
}: AddGroupToMatterModalProps) {
  const handleSave = useAddMatterItems(afterSave, group);

  return (
    <AddToMatterModal
      onSave={handleSave}
      visible={visible}
      closeModal={closeModal}
      type="Group"
    />
  );
}

export const StyledModalButton = styled.button`
  background: var(--COLOR_BLUEY_GRAY);
  border-radius: 3px;
  color: white;
  font-family: 'BasisGrotesque', sans-serif;
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  font-weight: normal;
  line-height: normal;
  padding: 10px 28px 8px;
  & + & {
    margin-left: 12px;
  }
`;

export const StyledPrimaryModalButton = styled(StyledModalButton)`
  background: var(--COLOR_NICE_BLUE);
  &[disabled] {
    background: color-mix(in srgb, var(--COLOR_NICE_BLUE) 50%, transparent);
  }
`;

export const StyledMatterList = styled.ol`
  list-style-type: none;
  overflow: auto;
`;

export const StyledMatterModal = styled.section`
  background: var(--COLOR_DESERT_STORM);
  display: flex;
  flex-direction: column;
  height: calc(100% - 29px - 48px);
  padding: 29px 36px 48px;
  > header,
  > section {
    flex: 0 1 auto;
  }
  > section {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    overflow: hidden;
    > div {
      flex: 0 1 auto;
      p {
        margin-bottom: 24px;
      }
      &.filtered-results {
        flex: 1 1 auto;
        margin-top: 24px;
        overflow: auto;
        .status-message {
          padding: 0;
          min-height: 0;
        }
      }
      &.button-row {
        margin-top: 24px;
      }
    }
  }
`;

export const StyledModalHeader = styled(ModalHeader)`
  h2 {
    font-family: 'Zilla Slab', serif;
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 20px;
  }
`;
